import React, { useRef, useState, useEffect } from "react";
import { inject, observer } from "mobx-react";
import { withRouter } from "react-router-dom";
import LabelVideoStore from "store/labelVideoStore";
import Progression from "pages/common/Progression";
import { theme } from "./progressbar/theme";
import { videoLabelsToIncidents } from "./utils/videoLabelsToIncidents";
import ProgressBar from "./progressbar/index";
import Ajustor from "./components/Ajustor";
import VideoController from "./components/VideoController";
import IncidentPanel from "./components/IncidentPanel";
import VideoLabelisationHead from "./components/VideoLabelisationHead";
import IncompleteBatchDialog from "./components/IncompleteBatchDialog";
import FinishBatchDialog from "./components/FinishBatchDialog";
import GlobalContext from "../../GlobalContext";
import LabelerSnackbar from "pages/common/SnackBar";
import Snackbar from "@material-ui/core/Snackbar";

function VideoLabeler(props: {
  match: any;
  history: any;
  labelVideoStore?: LabelVideoStore;
}) {
  const {
    match: {
      params: { noBatch, noImage },
    },
    history,
    labelVideoStore,
  } = props;

  const { appFetch, user } = React.useContext(GlobalContext) as any;
  const userLogin = user?.login || user?.email || null;
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoHeight, setVideoHeight] = useState(470 * 0.7);
  const [initialHeight, setInitialHeight] = useState(470 * 0.7);

  const currentVideoIndex = Number(noImage);
  const videos = labelVideoStore?.videos || [];
  const currentVideo = videos[currentVideoIndex];

  const handlePreviousVideo = () => {
    if (currentVideoIndex > 0) {
      history.push(`/labelerVideo/${noBatch}/${currentVideoIndex - 1}`);
    }
  };

  const handleNextVideo = () => {
    if (currentVideoIndex < videos.length - 1) {
      history.push(`/labelerVideo/${noBatch}/${currentVideoIndex + 1}`);
    }
  };

  const handleSaveLabels = () => {
    labelVideoStore?.saveLabels(
      appFetch,
      history,
      noBatch,
      currentVideoIndex,
      userLogin
    );
  };

  const handleSkipVideo = (motif: string) => {
    labelVideoStore?.skipVideo(
      appFetch,
      history,
      noBatch,
      currentVideoIndex,
      motif,
      userLogin
    );
  };

  useEffect(() => {
    labelVideoStore?.fetchInitialData(appFetch, noBatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsPlaying(false);
    labelVideoStore?.setCurrentFrame(0);
    labelVideoStore?.setTotalFrames(0);
    labelVideoStore?.setCurrentTime(0);
    labelVideoStore?.setDuration(0);
    if (videoRef.current) {
      ((videoRef.current as unknown) as HTMLVideoElement).pause();
      ((videoRef.current as unknown) as HTMLVideoElement).currentTime = 0;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noImage]);

  useEffect(() => {
    if (currentVideo === undefined) {
      labelVideoStore?.setIncidents([]);
      labelVideoStore?.setPredictionIncidents([]);
      return;
    }

    const fps = currentVideo.fps || 25;
    labelVideoStore?.setFps(fps);
    labelVideoStore?.setVideoMotif(currentVideo.motif || null);

    const videoLabels: any[] = currentVideo.motif
      ? []
      : currentVideo.labels?.videoLabels || [];
    const incidents = videoLabelsToIncidents(
      videoLabels,
      String(currentVideo.id),
      fps,
      labelVideoStore?.incidentClasses ?? [],
      { defaultColor: theme.colors.background }
    );
    labelVideoStore?.setIncidents(incidents);
    labelVideoStore?.loadPredictionIncidentsFromVideo(currentVideo, fps);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noImage, currentVideo?.id, labelVideoStore?.incidentClasses]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === "ArrowRight") {
        event.preventDefault();
        handleFrameChange(1);
      } else if (event.code === "ArrowLeft") {
        event.preventDefault();
        handleFrameChange(-1);
      } else if (event.code === "Space") {
        event.preventDefault();
        if (videoRef.current) {
          const video = (videoRef.current as unknown) as HTMLVideoElement;
          if (video.paused) {
            video.play();
            setIsPlaying(true);
          } else {
            video.pause();
            setIsPlaying(false);
          }
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    const video = videoRef.current;

    const updateProgress = () => {
      if (labelVideoStore?.fps && labelVideoStore?.fps > 0 && video) {
        labelVideoStore?.setCurrentTime(
          ((video as unknown) as HTMLVideoElement).currentTime
        );
        labelVideoStore?.setCurrentFrame(
          Math.round(
            ((video as unknown) as HTMLVideoElement).currentTime *
              labelVideoStore?.fps
          )
        );
      }
    };

    const onLoadedMetadata = () => {
      if (video) {
        labelVideoStore?.setDuration(
          ((video as unknown) as HTMLVideoElement).duration
        );
        labelVideoStore?.setTotalFrames(
          Math.round(
            ((video as unknown) as HTMLVideoElement).duration *
              labelVideoStore?.fps
          )
        );
      }
    };

    const onEnded = () => setIsPlaying(false);

    if (video) {
      ((video as unknown) as HTMLVideoElement).addEventListener(
        "timeupdate",
        updateProgress
      );
      ((video as unknown) as HTMLVideoElement).addEventListener(
        "loadedmetadata",
        onLoadedMetadata
      );
      ((video as unknown) as HTMLVideoElement).addEventListener(
        "ended",
        onEnded
      );
    }
    return () => {
      if (video) {
        ((video as unknown) as HTMLVideoElement).removeEventListener(
          "timeupdate",
          updateProgress
        );
        ((video as unknown) as HTMLVideoElement).removeEventListener(
          "loadedmetadata",
          onLoadedMetadata
        );
        ((video as unknown) as HTMLVideoElement).removeEventListener(
          "ended",
          onEnded
        );
      }
    };
  }, [labelVideoStore?.fps, noImage]);

  const handlePlay = () => {
    if (videoRef.current) {
      ((videoRef.current as unknown) as HTMLVideoElement).play();
      setIsPlaying(true);
    }
  };

  const handlePause = () => {
    if (videoRef.current) {
      ((videoRef.current as unknown) as HTMLVideoElement).pause();
      setIsPlaying(false);
    }
  };

  const handleFrameChange = (direction: number) => {
    const video = videoRef.current;
    if (video && labelVideoStore?.fps && labelVideoStore?.fps > 0) {
      const frameDuration = 1 / labelVideoStore?.fps;
      ((video as unknown) as HTMLVideoElement).currentTime +=
        direction * frameDuration;
    }
  };

  const handleSeek = (newFrame: number) => {
    labelVideoStore?.setCurrentFrame(newFrame);
    if (videoRef.current && labelVideoStore?.fps && labelVideoStore?.fps > 0) {
      ((videoRef.current as unknown) as HTMLVideoElement).currentTime =
        newFrame / labelVideoStore?.fps;
    }
  };

  const handleDrag = (deltaY: number) => {
    const newHeight = initialHeight + deltaY;
    setVideoHeight(
      Math.max(100, Math.min(newHeight, window.innerHeight - 200))
    );
  };

  const handleDragStart = (_startY: number) => {
    setInitialHeight(videoHeight);
  };

  return (
    <div
      style={{
        marginTop: "60px",
        padding: "1rem",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        overflow: "auto",
        width: "100%",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          width: "100%",
          gap: "1.25rem",
        }}
      >
        <div style={{ width: "70%" }}>
          <VideoLabelisationHead
            currentIndex={currentVideoIndex}
            total={videos.length}
            nomBatch={labelVideoStore?.nomBatch || ""}
            lastLabeledBy={currentVideo?.lastLabeledBy}
            isLabeled={
              (currentVideo?.labels?.videoLabels?.length || 0) > 0 ||
              !!currentVideo?.motif
            }
            onPrevious={handlePreviousVideo}
            onNext={handleNextVideo}
            prevDisabled={
              currentVideoIndex <= 0 ||
              !!labelVideoStore?.isLoadingSave ||
              !!labelVideoStore?.justSaved ||
              !!labelVideoStore?.incidents.some((inc) => !inc.isFinished)
            }
            nextDisabled={
              currentVideoIndex >= videos.length - 1 ||
              !!labelVideoStore?.isLoadingSave ||
              !!labelVideoStore?.justSaved ||
              !!labelVideoStore?.incidents.some((inc) => !inc.isFinished)
            }
          />
        </div>
        <div style={{ width: "30%", display: "flex", flexDirection: "row", alignItems: "flex-start", paddingTop: "4px" }}>
          <Progression
            total={videos.length}
            nbrLabelisees={
              videos.filter(
                (video: any) =>
                  video.labels?.videoLabels?.length > 0 || video.motif !== null
              ).length
            }
            showHelp={false}
            style={{ padding: 0, justifyContent: "flex-start" }}
            isVideo
          />
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "row", gap: "1.25rem" }}>
        {/* VIDEO CONTAINER */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "0px",
            width: "70%",
            position: "relative",
          }}
        >
          {/* VIDEO */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "black",
              borderRadius: "8px 8px 0px 0px",
            }}
          >
            <video
              ref={videoRef}
              key={currentVideoIndex}
              src={currentVideo?.url}
              style={{
                maxWidth: "100%",
                height: videoHeight,
                transition: "height 0.1s ease-out",
                backgroundColor: "black",
              }}
            />
          </div>

          <Ajustor onDrag={handleDrag} onDragStart={handleDragStart} />

          {/* LABELER */}
          <div
            style={{
              borderRadius: "0px 0px 8px 8px",
              backgroundColor: "#f3f3f3",
              height: `calc(100vh)`,
              transition: "height 0.1s ease-out",
            }}
          >
            <VideoController
              isPlaying={isPlaying}
              onPlay={handlePlay}
              onPause={handlePause}
              onFrameChange={handleFrameChange}
              labelVideoStore={labelVideoStore}
            />
            <ProgressBar onFrameChange={handleSeek} />
          </div>
        </div>

        {/* INCIDENT PANEL */}
        <IncidentPanel
          labelVideoStore={labelVideoStore}
          onPause={handlePause}
          onSave={handleSaveLabels}
          onSkip={handleSkipVideo}
        />
      </div>

      <LabelerSnackbar
        open={labelVideoStore?.justSaved || false}
        onClose={() => {
          labelVideoStore?.setJustSaved(false);
          labelVideoStore?.setSaveFeedbackMessage("");
        }}
        variant="success"
        message={
          labelVideoStore?.saveFeedbackMessage || "Vidéo labélisée"
        }
      />

      <Snackbar
        open={labelVideoStore?.isError || false}
        message={labelVideoStore?.message || ""}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        ContentProps={{
          style: { backgroundColor: "#f44336", justifyContent: "center" },
        }}
        onClose={() => labelVideoStore?.setIsError(false)}
      />

      <IncompleteBatchDialog
        open={labelVideoStore?.incompleteDialogOpen || false}
        onStay={() => labelVideoStore?.setIncompleteDialogOpen(false)}
        onGoToUnlabeled={() => {
          labelVideoStore?.setIncompleteDialogOpen(false);
          const unlabeledIndex = labelVideoStore?.firstUnlabeledIndex;
          if (unlabeledIndex !== null && unlabeledIndex !== undefined) {
            history.push(`/labelerVideo/${noBatch}/${unlabeledIndex}`);
          }
        }}
      />

      <FinishBatchDialog
        open={labelVideoStore?.finishDialogOpen || false}
        onConfirm={() => {
          labelVideoStore?.setFinishDialogOpen(false);
          history.push("/batches/0?sort=createdDate,desc");
        }}
      />
    </div>
  );
}

export default withRouter(inject("labelVideoStore")(observer(VideoLabeler)));
